import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { provideApi } from './api';
import { isStaleChunkError, shouldReloadForStaleChunk } from './core/errors/stale-chunk-error';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthFacade } from './core/services/auth.facade';
import { provideAppIcons } from './core/icons/icon-registry';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // Anchors del catálogo /ui (tarea 17.3): navegar a un fragmento (#colores) hace scroll a
      // la sección; scrollPositionRestoration conserva el comportamiento estándar de recordar la
      // posición al usar atrás/adelante.
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      // Tras un despliegue, una pestaña abierta con la versión anterior pide chunks lazy cuyo
      // hash ya no existe ("Failed to fetch dynamically imported module"): recarga completa a la
      // URL destino para traer el index.html nuevo, con guard anti-bucle en sessionStorage.
      withNavigationErrorHandler((navigationError) => {
        if (
          isStaleChunkError(navigationError.error) &&
          shouldReloadForStaleChunk(window.sessionStorage, Date.now())
        ) {
          window.location.assign(navigationError.url);
        }
      }),
    ),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideApi(),
    provideAnimationsAsync(),
    provideAppIcons(),
    // Silent refresh on load: exchanges the HttpOnly refresh cookie (if any) for an access
    // token before the app renders, so a reload doesn't bounce an already-logged-in user to
    // /login. Never rejects — no cookie/expired cookie just resolves to null.
    provideAppInitializer(() => firstValueFrom(inject(AuthFacade).silentRefresh())),
  ],
};
